import { useState, type ReactNode } from 'react';
import { CornerDownRight, Scale } from 'lucide-react';
import {
  DISPUTE_CHOICE_LABELS,
  UNCLEAR_CHOICE_LABELS,
  sendsBack,
  type DisputeAnswer,
  type DisputeChoice,
  type DisputedFinding,
  type FactoryDispute,
} from '@shared/factory-dispute';
import { disputeAnswerText } from './dispute-answer';
import { ChoiceCardGroup, type ChoiceCardOption } from '@/components/ChoiceCard';
import { ExplainedValue } from '@/components/ExplainedValue';
import { StatusBadge } from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { GrowingTextarea } from '@/components/GrowingTextarea';
import { FoldingCard } from './FoldingCard';
import type { ConcernTags } from '@shared/decision-concern';
import { ConcernBadge } from './ConcernBadge';

export interface DisputePick {
  number: number;
  choice: DisputeChoice;
  note: string;
}

/** What goes back to the builder, under each option's own description. */
const LEAVE_THEN = 'Nothing goes back to the builder.';
const SEND_THEN = 'The task goes back to the builder, in the same thread.';

/** The builder's pick: what the AI suggests, so the ai accent, not a status. */
const recommended = (
  <Badge variant="ai" size="sm">
    Recommended
  </Badge>
);

/**
 * SF-T-300: a finding's options, each saying what it would do for this finding (the builder's `leave`,
 * `fix` and `other`), with the one it recommends marked. A finding from before SF-T-300 has none of
 * that, and gets the general descriptions.
 */
function findingOptions(f: DisputedFinding): ChoiceCardOption<DisputeChoice>[] {
  const describe = (what: string | undefined, then: string, fallback: string) =>
    what ? (
      <>
        {what} <span className="text-muted-foreground/80">{then}</span>
      </>
    ) : (
      fallback
    );
  const options: ChoiceCardOption<DisputeChoice>[] = [
    {
      value: 'leave',
      title: DISPUTE_CHOICE_LABELS.leave,
      description: describe(f.leave, LEAVE_THEN, 'The PR keeps it as it is; nothing goes back to the builder.'),
      badge: f.recommend === 'leave' ? recommended : undefined,
    },
    {
      value: 'fix',
      title: DISPUTE_CHOICE_LABELS.fix,
      description: describe(f.fix, SEND_THEN, 'The task goes back to the builder, in the same thread, to fix it on this PR.'),
      badge: f.recommend === 'fix' ? recommended : undefined,
    },
  ];
  if (f.other) {
    options.push({
      value: 'other',
      title: f.other.label,
      description: describe(f.other.detail ?? 'The builder’s own option.', SEND_THEN, ''),
      badge: f.recommend === 'other' ? recommended : undefined,
    });
  }
  return options;
}

/** An unclear review (the builder didn't report what it did): one decision about the whole review. */
const UNCLEAR_OPTIONS: ChoiceCardOption<DisputeChoice>[] = [
  {
    value: 'leave',
    title: UNCLEAR_CHOICE_LABELS.leave,
    description: 'You’ve checked the PR’s “Codex review” section and are happy with it. Nothing goes back to the builder, and the PR’s badge shows it was accepted.',
  },
  {
    value: 'fix',
    title: UNCLEAR_CHOICE_LABELS.fix,
    description:
      'The task goes back to the builder, in the same thread, to say what it did about each finding (fixed, not real or disputed). Anything it disputes then comes back here to settle.',
  },
];

/** The note box's placeholder: on Leave it's kept with the decision; otherwise the builder reads it. */
const notePlaceholder = (choice: DisputeChoice, unclear: boolean) =>
  sendsBack(choice) ? (unclear ? 'Anything for the builder to look at (optional)' : 'Note for the builder (optional)') : 'Note for the record (optional; the builder doesn’t get it)';

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/**
 * SF-T-74: a run's disputed review findings (a `factory-dispute` block in its handoff note), as a card
 * in the task's Decisions. Someone who can comment picks, for each finding, Leave it as the builder
 * says or Ask the builder to fix it, and settles them in one go (onSettle). A finding already settled
 * by deciding its matching suggestion shows how. Once settled, answered in a comment, or replaced by a
 * newer review, or without `onSettle`, it shows the findings and their answers. An unclear review (the
 * builder didn't report what it did) is one choice: Accept the PR as it is, or Ask the builder to report
 * its verdict. When the decisions were saved but the outcome didn't finish (`unfinished`), Finish
 * settling calls onSettle with no picks, which applies it.
 */
export function FactoryDisputeCard({
  dispute,
  answers = new Map(),
  closedBy = null,
  unfinished = false,
  settled: settledGiven,
  fold = false,
  id,
  meta,
  onSettle,
  onPicksChange,
  concerns,
}: {
  dispute: FactoryDispute;
  /** Answers given so far, by finding number (1-based). */
  answers?: ReadonlyMap<number, DisputeAnswer>;
  /** Closed before it was settled: a person's comment (it went to the builder), or a newer review. */
  closedBy?: 'comment' | 'newer' | null;
  /** Every finding answered, but the outcome (badge, waits) didn't finish: Finish settling applies it (disputeUnfinished). */
  unfinished?: boolean;
  /** Settled whatever the answers show (disputeComplete: the factory's settled note came, though a decision behind it was undone). */
  settled?: boolean;
  /** Starts folded to its header line, which opens it (T-72: a settled review in the task's Decisions). */
  fold?: boolean;
  /** For a pointer in the comments to scroll to it (showTaskDecisions). */
  id?: string;
  /** Called with how many findings have a pick not settled yet, as it changes (a dialog's unsaved-work guard). */
  onPicksChange?: (count: number) => void;
  /** Who posted it and when, at the end of the header line. */
  meta?: ReactNode;
  /** Settles the findings still open; resolves true when it worked. Leave out where nobody can (a preview). */
  onSettle?: (picks: DisputePick[]) => Promise<boolean>;
  /** SF-T-182: Jev's Product or Architecture tags for the items (payloadConcerns of the message), a badge on each. */
  concerns?: ConcernTags;
}) {
  const [picks, setPicks] = useState<Record<number, { choice?: DisputeChoice; note: string }>>({});
  const [saving, setSaving] = useState(false);
  const total = dispute.findings.length;
  const open = dispute.findings.map((_f, i) => i + 1).filter((n) => !answers.has(n));
  const settled = settledGiven ?? open.length === 0;
  const editable = !settled && !closedBy && Boolean(onSettle);
  const complete = open.every((n) => picks[n]?.choice);
  const set = (n: number, change: Partial<{ choice: DisputeChoice; note: string }>) => {
    const next = { ...picks, [n]: { choice: picks[n]?.choice, note: picks[n]?.note ?? '', ...change } };
    setPicks(next);
    onPicksChange?.(Object.values(next).filter((p) => p.choice).length);
  };

  const settle = async () => {
    if (!onSettle || !complete) return;
    setSaving(true);
    const ok = await onSettle(open.map((n) => ({ number: n, choice: picks[n].choice!, note: picks[n].note.trim() })));
    setSaving(false);
    if (ok) {
      setPicks({});
      onPicksChange?.(0);
    }
  };

  const finish = async () => {
    if (!onSettle) return;
    setSaving(true);
    await onSettle([]);
    setSaving(false);
  };

  const run = dispute.run !== null ? `R-${dispute.run}'s review` : 'The review';
  const unclear = Boolean(dispute.unclear);
  // Folded: what came of it, on one line.
  const summary = [...answers.entries()].map(([n, a]) => `${unclear ? '' : `${n}. `}${disputeAnswerText(a, unclear)}`).join(' · ');
  return (
    <FoldingCard
      id={id}
      fold={fold}
      summary={summary || undefined}
      header={
        <>
          <Scale className="size-4" aria-hidden />
          {unclear ? `${run}: what the builder did is unclear` : `${run}: ${plural(total, 'disputed finding')}`}
          {unfinished && !closedBy ? (
            <StatusBadge tone="warning" size="sm" dot>
              Settling didn’t finish
            </StatusBadge>
          ) : settled ? (
            <StatusBadge tone="success" size="sm">
              Settled
            </StatusBadge>
          ) : closedBy === 'comment' ? (
            <StatusBadge tone="neutral" size="sm">
              Answered in a comment
            </StatusBadge>
          ) : closedBy === 'newer' ? (
            <StatusBadge tone="neutral" size="sm">
              Replaced by a newer review
            </StatusBadge>
          ) : (
            <StatusBadge tone="warning" size="sm" dot>
              Needs settling
            </StatusBadge>
          )}
          {meta ? <span className="ml-auto font-normal">{meta}</span> : null}
        </>
      }
    >
      <ol className="space-y-3">
        {dispute.findings.map((f, i) => {
          const n = i + 1;
          const answer = answers.get(n);
          return (
            <li key={n} className="space-y-1.5">
              <p className="text-sm">
                <span className="text-muted-foreground">{n}. </span>
                {f.task ? (
                  <ExplainedValue className="mr-1.5 font-medium text-muted-foreground" explanation={`About ${f.task}, one of the sub-tasks sharing this PR`}>
                    {f.task}
                  </ExplainedValue>
                ) : null}
                <span className="font-medium">{f.title}</span>
                {f.where ? <code className="ml-1.5 rounded bg-muted px-1 py-0.5 font-mono text-2xs text-muted-foreground">{f.where}</code> : null}{' '}
                <ConcernBadge concerns={concerns} kind="dispute" item={n} />
              </p>
              {f.concern ? (
                <p className="pl-4 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">The reviewer’s concern:</span> {f.concern}
                </p>
              ) : null}
              {f.reason ? (
                <p className="pl-4 text-xs text-muted-foreground">
                  <span className={f.concern ? 'font-medium text-foreground' : undefined}>{f.concern ? 'Why the builder left it:' : 'The builder:'}</span> {f.reason}
                </p>
              ) : null}
              {f.suggestion && !f.other && !answer && !closedBy ? (
                <p className="pl-4 text-xs text-muted-foreground">
                  The same as its suggestion <code className="font-mono">{f.suggestion}</code>: deciding that suggestion settles this too.
                </p>
              ) : null}
              {answer ? (
                <GivenAnswer answer={answer} unclear={unclear} />
              ) : editable ? (
                <div className="space-y-2 pl-4">
                  <ChoiceCardGroup
                    size="sm"
                    label={unclear ? 'What to do with this review' : `What to do with “${f.title}”`}
                    value={picks[n]?.choice ?? ('' as DisputeChoice)}
                    onValueChange={(choice) => set(n, { choice })}
                    options={unclear ? UNCLEAR_OPTIONS : findingOptions(f)}
                    disabled={saving}
                  />
                  {picks[n]?.choice ? (
                    <GrowingTextarea
                      aria-label={`Note on “${f.title}” (optional)`}
                      placeholder={notePlaceholder(picks[n].choice!, unclear)}
                      value={picks[n]?.note ?? ''}
                      onChange={(e) => set(n, { note: e.target.value })}
                      disabled={saving}
                    />
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
      {unfinished && !closedBy && onSettle ? (
        <div className="flex items-center justify-end gap-2">
          <span className="text-xs text-muted-foreground">The decisions are saved; the badge and the wait weren’t updated.</span>
          <Button size="sm" loading={saving} onClick={() => void finish()}>
            Finish settling
          </Button>
        </div>
      ) : null}
      {editable ? (
        <div className="flex items-center justify-end gap-2">
          {!complete ? <span className="text-xs text-muted-foreground">{unclear ? 'Pick one to settle' : 'Pick for each finding to settle'}</span> : null}
          <Button size="sm" disabled={!complete} loading={saving} onClick={() => void settle()}>
            Settle
          </Button>
        </div>
      ) : null}
    </FoldingCard>
  );
}

function GivenAnswer({ answer, unclear }: { answer: DisputeAnswer; unclear: boolean }) {
  return (
    <p className="flex items-start gap-1.5 pl-4 text-sm font-medium">
      <CornerDownRight className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 whitespace-pre-line break-words">{disputeAnswerText(answer, unclear)}</span>
    </p>
  );
}

/** In the comments, a run's disputed findings as one line pointing to the task's Decisions, where they're settled. */
export function FactoryDisputePointer({ count, unclear = false, onShow }: { count: number; unclear?: boolean; onShow: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
      <Scale className="size-4 shrink-0" aria-hidden />
      <span>{unclear ? 'A review to settle' : count === 1 ? '1 disputed finding' : `${count} disputed findings`}</span>
      <Button variant="link" size="xs" className="h-auto p-0 text-xs" onClick={onShow}>
        Settle in Decisions
      </Button>
    </div>
  );
}
